<template>
  <CRow class="justify-content-center">
    <CSpinner v-if="state.isloading" color="primary" />
    <CCol v-if="activePass == null && !state.isloading" md="8" lg="6">
      <div>{{ $t("common.no_passes") }}</div>
    </CCol>

    <CCol
      v-if="!state.isloading && activePass"
      class="active-pass-page-content-col"
      md="12"
      lg="12"
    >
      <h1 class="sr-only">{{ $t("sidebar_items.active_pass") }}</h1>
      <div
        :class="[isPassLongRunning ? 'longRunning' : currentStatus]"
        class="active-pass rounded-30 pt-2"
        data-test-id="student-active-pass"
      >
        <!-- Today’s history: first in DOM so reading order matches visual prominence (badge is absolutely positioned top-left) -->
        <button
          type="button"
          class="active-pass-history-badge text-white border-0 p-0"
          data-test-id="student-active-pass-count"
          :aria-label="
            $t('common.a11y_todays_pass_history_button', {
              count: activePass.user.passes_for_today_count
            })
          "
          @click="openHistoryModal()"
        >
          <span aria-hidden="true">{{
            activePass.user.passes_for_today_count
          }}</span>
        </button>

        <!-- Pass content based on status -->
        <QueuePassView
          v-if="currentStatus === 'queue'"
          :active-pass="activePass"
          :comments="comments"
          :time-in-request="timeInRequest"
          :time-in-pass="timeInPass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :pin-attempts-count="pinAttemptsCount"
          :is-active-auto-check-in-module="isActiveAutoCheckInModule"
          @open-cancel-modal="openCancelModal"
        />

        <WaitingPassView
          v-if="currentStatus === 'waiting'"
          :active-pass="activePass"
          :comments="comments"
          :time-in-request="timeInRequest"
          :time-in-pass="timeInPass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :pin-attempts-count="pinAttemptsCount"
          :is-active-auto-check-in-module="isActiveAutoCheckInModule"
          @open-cancel-modal="openCancelModal"
          @auto-approve="autoApprove"
        />

        <ApprovedPassView
          v-if="currentStatus === 'approved'"
          :active-pass="activePass"
          :comments="comments"
          :time-in-pass="timeInPass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :pin-attempts-count="pinAttemptsCount"
          :is-active-auto-check-in-module="isActiveAutoCheckInModule"
          @auto-pause="autoPause"
          @show-check-in="showCheckInModal"
        />

        <ExpiredPassView
          v-if="currentStatus === 'expired'"
          :active-pass="activePass"
          :comments="comments"
          :time-in-pass="timeInPass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :pin-attempts-count="pinAttemptsCount"
        />

        <!-- Parent Pass Info -->
        <div v-if="activePass.parent">
          <hr class="bg-white" id="active-pass-hr" />
          <PassFromToInfo
            :pass="activePass.parent"
            :current-status="currentStatus"
            :is-pass-long-running="isPassLongRunning"
            :is-pass-system-ended="isPassSystemEnded"
            :actions="state.actions"
            @open-stuff-modal="openStuffModal"
          />
        </div>

        <!-- No Parent Pass Info -->
        <hr
          v-if="activePass.parent"
          class="bg-white"
          id="active-pass-hr-bottom"
        />

        <PassFromToInfo
          :pass="activePass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :is-pass-system-ended="isPassSystemEnded"
          :actions="state.actions"
          @open-stuff-modal="openStuffModal"
        />

        <!-- Pass Status Display -->
        <PassStatusDisplay
          :active-pass="activePass"
          :current-status="currentStatus"
          :is-pass-long-running="isPassLongRunning"
          :pass-statuses="state.passStatuses"
        />
      </div>
    </CCol>
  </CRow>
</template>

<script>
import helpersJS from "@/helpers/index.js"
import queueUtils from "@/helpers/queueUtils"
import ApprovedPassView from "@/v3components/ActivePass/ApprovedPassView.vue"
import ExpiredPassView from "@/v3components/ActivePass/ExpiredPassView.vue"
import PassFromToInfo from "@/v3components/ActivePass/PassFromToInfo.vue"
import PassStatusDisplay from "@/v3components/ActivePass/PassStatusDisplay.vue"
import QueuePassView from "@/v3components/ActivePass/QueuePassView.vue"
import WaitingPassView from "@/v3components/ActivePass/WaitingPassView.vue"
import moment from "moment-timezone"
import {
  computed,
  defineAsyncComponent,
  inject,
  onMounted,
  reactive,
  onUnmounted,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const CancelPassModal = defineAsyncComponent(
  () => import("@/v3components/Forms/Students/CancelPassModal.vue")
)
const StudentPassHistory = defineAsyncComponent(
  () => import("@/v3components/Forms/Students/StudentPassHistory.vue")
)
const AdultAptPassActionModal = defineAsyncComponent(
  () => import("@/v3components/Forms/Students/AdultAptPassActionModal.vue")
)
const AdultAptPassCheckInModal = defineAsyncComponent(
  () => import("@/v3components/Forms/Students/AdultAptPassCheckInModal.vue")
)

export default {
  name: "ActivePass",
  components: {
    QueuePassView,
    WaitingPassView,
    ApprovedPassView,
    ExpiredPassView,
    PassFromToInfo,
    PassStatusDisplay
  },
  setup() {
    const store = useStore()
    const modal = inject("modal")
    const { t } = useI18n()

    const state = reactive({
      passStatuses: {
        queue: t("common.in_queue"),
        waiting: t("common.waiting_approval"),
        approved: t("common.active"),
        expired: t("common.ended"),
        canceled: t("common.canceled")
      },
      actions: {
        approved: 1,
        end: 2,
        arrived: 3,
        returning: 4,
        cancel: 5
      },
      counter: 1,
      showStuffModal: false,
      teacherPin: "",
      teacherNote: "",
      responseMessageTeacherApproval: null,
      responseMessageRoomApproval: null,
      modalAction: 1,
      isloading: false,
      updatePassHistory: true,
      showCheckInModal: false,
      showCancelModal: false,
      roomPin: ""
    })

    const isQueueModuleActive = computed(() => {
      return store.getters["queueSettings/isQueueModuleActive"]
    })
    const activePass = computed(() => store.getters["passes/activePass"])
    const staff = computed(() => store.getters["users/formListStaff"])
    const rooms = computed(() => store.getters["rooms/formListRooms"])
    const pinAttemptsCount = computed(
      () => store.getters["authentication/pinAttemptsCount"]
    )
    const currentUser = computed(() => store.getters["authentication/user"])
    const passHistories = computed(
      () => store.getters["passes/studentPassHistories"]
    )
    const comments = computed(() => {
      if (currentStatus.value === "queue") {
        const hasQueueComment =
          String(activePass?.value?.comment || "").trim()?.length > 0
        if (hasQueueComment) {
          return [
            {
              created_at: activePass?.value?.created_at,
              comment: activePass?.value?.comment || "",
              user: {
                first_name: currentUser?.value?.first_name || "",
                last_name: currentUser?.value?.last_name || ""
              }
            }
          ]
        }
        return []
      }
      return store.getters["comments/comments"]
    })
    const isActiveAutoCheckInModule = computed(
      () => store.getters["schools/isActiveAutoCheckInModule"]
    )

    const timeInRequest = computed(() => {
      if (activePass.value && activePass.value.pass_status != 0) {
        const duration = moment.duration(activePass.value.expire_after)
        let timeIn = 0
        if (!activePass.value.parent) {
          if (activePass.value.approved_at) {
            timeIn = moment(activePass.value.approved_at).valueOf()
          } else {
            timeIn = moment(activePass.value.requested_at).valueOf()
          }
        } else {
          if (activePass.value.parent.approved_at) {
            timeIn = moment(activePass.value.parent.approved_at).valueOf()
          }
        }
        // var requested_at = moment(activePass.value.requested_at).valueOf();
        const expired_at = timeIn + duration.asMilliseconds()
        const timeremain = moment(expired_at).diff(moment(), "seconds")

        if (timeremain <= 0 || !state.counter || !activePass.value) {
          return "00:00:00"
        }
        return helpersJS.secondsToTimeString(timeremain)
      }
      return null
    })

    const timeInPass = computed(() => {
      return state.counter &&
        activePass.value &&
        activePass.value.pass_status != 0
        ? helpersJS.secondsToTimeString(
            moment().diff(
              activePass.value.parent && activePass.value.parent.approved_at
                ? moment(activePass.value.parent.approved_at)
                : activePass.value.approved_at
                  ? moment(activePass.value.approved_at)
                  : moment(),
              "seconds"
            )
          )
        : ""
    })

    const fromToCollection = computed(() => {
      return staff.value && rooms.value ? staff.value.concat(rooms.value) : null
    })

    const isPassLongRunning = computed(() => {
      return (
        currentStatus.value !== "expired" &&
        ((activePass.value &&
          activePass.value.pass_status == 1 &&
          activePass.value.flagged_at !== null) ||
          (activePass.value.parent &&
            activePass.value.parent.flagged_at !== null))
      )
    })

    const isQueuePass = computed(() => {
      if (!isQueueModuleActive.value) {
        return false
      }
      return queueUtils.isPassStatusQueue(activePass.value)
    })

    const currentStatus = computed(() => {
      if (isQueuePass.value) {
        return "queue"
      }
      if (
        activePass.value &&
        activePass.value.pass_status == 1 &&
        activePass.value.approved_at === null &&
        !activePass.value.parent
      ) {
        return "waiting"
      }
      if (
        activePass.value &&
        activePass.value.pass_status == 1 &&
        activePass.value.approved_at === null &&
        activePass.value.parent
      ) {
        return "approved"
      }
      if (
        activePass.value &&
        activePass.value.pass_status == 1 &&
        activePass.value.approved_at !== null &&
        activePass.value.completed_at === null
      ) {
        return "approved"
      }
      return "expired"
    })

    const isLayoverPass = computed(() => {
      return activePass.value &&
        (activePass.value.to_type == "App\\Models\\User" ||
          (activePass.value.to && activePass.value.to.trip_type == "Layover"))
        ? true
        : false
    })

    const isPassSystemEnded = computed(() => {
      if (activePass.value) {
        return helpersJS.isPassSystemEnded(activePass.value)
      }
      return null
    })

    onMounted(() => {
      document.title = `${t("sidebar_items.active_pass")} · Securly Pass`

      state.isloading = true
      store.dispatch("passes/getStudentActivePass").then((response) => {
        state.isloading = false
        if (response.data && response.data.data && response?.data?.data?.id) {
          const data = response.data.data
          const params = {
            from_id: data.from_id,
            from_type: data.from_type,
            to_id: data.to_id,
            to_type: data.to_type
          }
          store.dispatch("passes/getActiveFlags", params)
        }
      })
      clearInterval(window.activePass)
      window.activePass = setInterval(() => {
        state.counter++
      }, 1000)
    })

    onUnmounted(() => {
      clearInterval(window.activePass)
    })

    const closeCheckInModal = () => {
      state.roomPin = ""
      state.showCheckInModal = false
    }

    const closeCancelModal = () => {
      state.showCancelModal = false
    }

    const openCancelModal = () => {
      modal.open(CancelPassModal, {
        size: "lg",
        title: "",
        props: {
          passID: activePass.value.id,
          isQueuePass: queueUtils.isPassStatusQueue(activePass.value)
        }
      })
    }

    const openHistoryModal = () => {
      modal.open(StudentPassHistory, {
        size: "lg",
        title: t("common.todays_history"),
        props: {
          setData: true
        },
        help: "todays_history"
      })
    }

    const closeStuffModal = () => {
      state.showStuffModal = false
      state.responseMessageTeacherApproval = null
      state.teacherPin = ""
      state.teacherNote = ""
      // v$.value.$reset() - Fixing eslint error as v$ is not defined
    }

    const openStuffModal = () => {
      modal.open(AdultAptPassActionModal, {
        size: "lg",
        title: "",
        props: {
          activePass: activePass.value,
          currentStatus: currentStatus.value,
          isLayoverPass: isLayoverPass.value
        }
      })
    }

    const showCheckInModal = () => {
      modal.open(AdultAptPassCheckInModal, {
        size: "lg",
        title: "",
        props: {
          activePass: activePass.value
        }
      })
    }

    const autoApprove = () => {
      store.dispatch("passes/autoApprovePass", {
        id: activePass.value.id,
        action: 1
      })
    }

    const autoPause = () => {
      store.dispatch("passes/autoApprovePass", {
        id: activePass.value.id,
        action: 2
      })
    }

    const deleteActivePassBlock = () => {
      store.commit("passBlocks/DELETE_ACTIVE_PASS_BLOCK")
    }

    const resetTeacherApproveMessage = () => {
      state.responseMessageTeacherApproval = null
    }

    watch(
      () => activePass.value,
      () => {
        if (isQueuePass.value) {
          return
        }
        const getCommentData = {
          passId: activePass.value.parent
            ? activePass.value.parent.id
            : activePass.value.id,
          commentableType: "App\\Models\\Pass"
        }
        store.dispatch("comments/getPassComments", getCommentData)
      }
    )

    const isLongRunning = (pass) => {
      return (
        pass !== "expired" &&
        ((pass && pass.pass_status == 1 && pass.flagged_at !== null) ||
          (pass?.parent && pass?.parent?.flagged_at !== null))
      )
    }

    watch(
      () => activePass.value,
      (newVal, oldVal) => {
        if (isQueuePass.value) {
          return
        }
        if (
          (!isLongRunning(oldVal) && isLongRunning(newVal)) ||
          oldVal?.flagged_at != newVal?.flagged_at
        ) {
          return
        }

        if (newVal != oldVal) {
          modal.close()
        }
      }
    )

    watch(
      () => timeInPass.value,
      () => {
        if (isQueuePass.value) {
          return
        }
        if (
          activePass.value.pass_status != 0 &&
          (activePass.value.flagged_at == null ||
            (activePass.value.parent &&
              activePass.value.parent.flagged_at == null))
        ) {
          const timeInPassArr = timeInPass.value.split(":") // split it at the colons
          const timeInPassSeconds =
            +timeInPassArr[0] * 60 * 60 +
            +timeInPassArr[1] * 60 +
            +timeInPassArr[2]

          const flagAfterArr = activePass.value.flag_after.split(":")
          const flagAfterSeconds =
            +flagAfterArr[0] * 60 * 60 +
            +flagAfterArr[1] * 60 +
            +flagAfterArr[2]

          if (timeInPassSeconds >= flagAfterSeconds) {
            store.commit("passes/SET_ACTIVE_PASS", {
              ...activePass.value,
              flagged_at: moment()
            })
            if (activePass.value.parent) {
              store.commit("passes/SET_ACTIVE_PASS", {
                ...activePass.value,
                parent: { ...activePass.value.parent, flagged_at: moment() }
              })
            }
          }
        }
      }
    )

    watch(
      () => timeInRequest.value,
      () => {
        if (isQueuePass.value) {
          return
        }
        if (
          timeInRequest.value == "00:00:00" &&
          activePass.value.pass_status != 0
        ) {
          const temp = JSON.parse(JSON.stringify(activePass.value))
          temp.pass_status = 0
          temp.ended_at = moment().format("YYYY-MM-DD HH:mm:ss")
          store.commit("passes/SET_ACTIVE_PASS", temp)
          const elem = {
            id: activePass.value.id,
            data: {
              action: 6
            }
          }
          store.dispatch("passes/updatePass", elem).then(() => {
            state.responseMessageTeacherApproval = {
              type: "success",
              message: t("common.successfully_updated")
            }
            state.teacherPin = ""
            state.teacherNote = ""
          })
        }
      }
    )

    return {
      state,
      activePass,
      staff,
      rooms,
      pinAttemptsCount,
      passHistories,
      comments,
      isActiveAutoCheckInModule,
      timeInRequest,
      timeInPass,
      fromToCollection,
      isPassLongRunning,
      currentStatus,
      isLayoverPass,
      isPassSystemEnded,
      closeCheckInModal,
      closeCancelModal,
      openHistoryModal,
      closeStuffModal,
      openStuffModal,
      autoApprove,
      autoPause,
      deleteActivePassBlock,
      resetTeacherApproveMessage,
      openCancelModal,
      showCheckInModal
    }
  }
}
</script>
<style scoped lang="scss">
/* Route /passes/activepass — space below app header (this view only) */
.active-pass-page-content-col {
  padding-top: 15px;
}

.stuff-only-popup-buttons {
  display: flex;
  justify-content: space-between;
  margin: 0 -10px;

  @media screen and (max-width: 768px) {
    margin: 0px;
  }

  button.btn {
    display: flex;
    align-items: center;
    width: 130px !important;
    padding: 5px 10px !important;

    @media screen and (max-width: 768px) {
      width: 100% !important;
    }

    &:before,
    & .flaticon-forbidden,
    & .flaticon-cancel,
    & .flaticon-check {
      width: 25px;
      height: 25px;
      background-size: 100%;
      font-size: 23px;
    }
  }
}

.comments-section-warning {
  font-size: 14px;
}
</style>
